import { ClientMsgKind, PlayerStatus } from '@infiltre/shared';
import { selectRoom, selectValidVoteTargets, useMyPlayer } from '@/entities/room';
import { PlayerReadyRoster, VoteBoard } from '@/features/game-round';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { useT } from '@/shared/lib/i18n';

export const OnlineVoteOrchestrator = () => {
  const t = useT();
  const playSound = useSound();
  const room = useGameStore(selectRoom);
  const driver = useSessionStore((s) => s.driver);
  const identity = useSessionStore((s) => s.identity);
  const me = useMyPlayer();
  const candidates = useGameStore((s) => (me ? selectValidVoteTargets(s, me.id) : []));

  if (!room || !driver || !identity) return null;

  const aliveIds = room.players.filter((p) => p.status === PlayerStatus.Alive).map((p) => p.id);
  const inactiveIds = room.players.filter((p) => p.status !== PlayerStatus.Alive).map((p) => p.id);
  const voterIds = room.votes.map((v) => v.voter);
  const pending = Math.max(0, aliveIds.length - voterIds.length);

  const renderRoster = () => (
    <PlayerReadyRoster
      players={room.players}
      readyIds={voterIds}
      inactiveIds={inactiveIds}
      eyebrow={t('online.vote.waiting.eyebrow')}
      title={t('online.vote.waiting.title')}
      descriptor={t('online.vote.waiting.descriptor', { pending })}
      centerLabel={t('online.vote.waiting.center_label')}
      selfId={me?.id}
    />
  );

  if (!me || me.status !== PlayerStatus.Alive) {
    return renderRoster();
  }

  const alreadyVoted = room.votes.some((v) => v.voter === me.id);
  if (alreadyVoted) {
    return renderRoster();
  }

  return (
    <div className="flex h-full flex-col">
      <VoteBoard
        voter={me}
        candidates={candidates}
        onVote={(target) => {
          playSound(SoundCue.VoteCast);
          driver.send({
            kind: ClientMsgKind.CastVote,
            actorPlayerId: me.id,
            targetPlayerId: target,
          });
        }}
      />
    </div>
  );
};
